<template>
    <div class="min-h-screen flex relative overflow-hidden font-inter bg-white">
        <!-- Organizations List -->
        <div class="flex-1 flex items-center justify-center relative z-10 bg-white/95 p-6">
            <div class="w-full max-w-md space-y-8">
                <!-- Logo -->
                <div class="flex justify-center mb-8">
                    <div class="flex items-center gap-3">
                        <img
                            src="http://192.168.1.4/storage/app/public/business/2024-01-18-65a9885147d16.png"
                            class="h-[70px]"
                            alt="Logo"
                        />
                    </div>
                </div>

                <!-- Title -->
                <div class="text-center">
                    <h2 class="text-2xl font-semibold text-gray-800">Escolha uma Organização</h2>
                    <p class="text-gray-500 text-sm mt-2">
                        Selecione uma organização para acessar o sistema
                    </p>
                </div>

                <!-- Loading State -->
                <div v-if="loading" class="space-y-4">
                    <!-- Skeleton Items -->
                    <div
                        v-for="n in 3"
                        :key="n"
                        class="bg-white border-2 border-gray-200 rounded-xl p-4 animate-pulse"
                    >
                        <div class="flex items-center gap-4">
                            <!-- Skeleton Avatar -->
                            <div class="w-14 h-14 rounded-full bg-gray-200 flex-shrink-0"></div>

                            <!-- Skeleton Text -->
                            <div class="flex-1 space-y-2">
                                <div class="h-4 bg-gray-200 rounded w-3/4"></div>
                                <div class="h-3 bg-gray-200 rounded w-1/2"></div>
                            </div>

                            <!-- Skeleton Icon -->
                            <div class="w-5 h-5 bg-gray-200 rounded flex-shrink-0"></div>
                        </div>
                    </div>
                </div>

                <!-- Error State -->
                <div v-else-if="error" class="text-center space-y-4">
                    <div class="bg-red-50 border border-red-200 rounded-xl p-4">
                        <div class="text-red-600 mb-2">
                            <svg
                                class="w-8 h-8 mx-auto"
                                fill="none"
                                stroke="currentColor"
                                viewBox="0 0 24 24"
                            >
                                <path
                                    stroke-linecap="round"
                                    stroke-linejoin="round"
                                    stroke-width="2"
                                    d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                                />
                            </svg>
                        </div>
                        <p class="text-red-700 font-medium">Erro ao carregar organizações</p>
                        <p class="text-red-600 text-sm mt-1">{{ error }}</p>
                        <button
                            @click="loadOrganizations"
                            class="mt-3 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors text-sm"
                        >
                            Tentar Novamente
                        </button>
                    </div>
                </div>

                <!-- Empty State -->
                <div v-else-if="organizations.length === 0" class="text-center space-y-4">
                    <div class="bg-gray-50 border border-gray-200 rounded-xl p-6">
                        <div class="text-gray-400 mb-3">
                            <svg
                                class="w-12 h-12 mx-auto"
                                fill="none"
                                stroke="currentColor"
                                viewBox="0 0 24 24"
                            >
                                <path
                                    stroke-linecap="round"
                                    stroke-linejoin="round"
                                    stroke-width="2"
                                    d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4"
                                />
                            </svg>
                        </div>
                        <p class="text-gray-700 font-medium">Nenhuma organização encontrada</p>
                        <p class="text-gray-500 text-sm mt-1">
                            Entre em contato com o administrador
                        </p>
                    </div>
                </div>

                <!-- Organizations List -->
                <div v-else class="space-y-4">
                    <!-- Organization Item -->
                    <div
                        v-for="org in organizations"
                        :key="org.id"
                        @click="handleChooseOrganization(org)"
                        class="group relative bg-white border-2 border-gray-200 rounded-xl p-4 hover:border-primary hover:shadow-lg transition-all duration-300 cursor-pointer"
                    >
                        <div class="flex items-center gap-4">
                            <!-- Organization Info -->
                            <div class="flex-1 min-w-0">
                                <h3 class="font-semibold text-gray-800 text-lg truncate">
                                    {{ org.name }}
                                </h3>
                                <p v-if="org.document" class="text-gray-500 text-xs mt-1">
                                    {{ formatDocument(org.document) }}
                                </p>
                                <p v-if="org.email" class="text-gray-400 text-xs mt-1">
                                    {{ org.email }}
                                </p>
                            </div>

                            <!-- Icons -->
                            <div class="flex items-center gap-2 flex-shrink-0">
                                <!-- Chevron icon -->
                                <div
                                    class="text-gray-400 group-hover:text-primary transition-colors transform group-hover:translate-x-1"
                                >
                                    <svg
                                        class="w-5 h-5"
                                        fill="none"
                                        stroke="currentColor"
                                        viewBox="0 0 24 24"
                                    >
                                        <path
                                            stroke-linecap="round"
                                            stroke-linejoin="round"
                                            stroke-width="2"
                                            d="M9 5l7 7-7 7"
                                        />
                                    </svg>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                <!-- Action Buttons -->
                <div class="flex items-center justify-between pt-6 border-t border-gray-200">
                    <button
                        @click="goBack"
                        class="px-6 py-2.5 text-gray-600 hover:text-gray-800 font-medium transition-colors flex items-center gap-2 hover:bg-gray-50 rounded-lg"
                        :disabled="loading"
                    >
                        <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 19l-7-7 7-7" />
                        </svg>
                        Voltar
                    </button>

                    <div class="flex gap-3">
                        <button
                            @click="logout(); $router.push({ name: 'login' })"
                            class="px-6 py-2.5 bg-white text-red-500 font-medium rounded-lg hover:bg-red-600 focus:outline-none border-2 border-red-500 transition-all duration-200 flex items-center gap-2 hover:text-white hover:border-white"
                            :disabled="loading"
                        >
                            <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" />
                            </svg>
                            Sair
                        </button>
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>

<script lang="ts" setup>
import { ref, onMounted } from 'vue'
import { useRouter } from 'vue-router'
import axiosClient from '@/api/axios'
import { setOrganization, removeAuthCookies, removeStorageItems } from '@/utils/auth'
import { useToast } from 'vue-toastification'

const router = useRouter()
const toast = useToast()

// Reactive data
const organizations = ref<any[]>([])
const loading = ref(false)
const error = ref<string | null>(null)

// Load organizations on component mount
const loadOrganizations = async () => {
    loading.value = true
    error.value = null

    try {
        const response = await axiosClient.get('/organizations')
        organizations.value = response.data.data || response.data || []
    } catch (err: any) {
        console.error('Error loading organizations:', err)
        error.value = err.response?.data?.errors?.[0]?.message || err.response?.data?.message || 'Erro ao carregar organizações'
    } finally {
        loading.value = false
    }
}

// Confirm organization selection
const handleChooseOrganization = async (selectedOrg: any) => {
    try {
        setOrganization(selectedOrg)
        toast.success(`Organização "${selectedOrg.name}" selecionada`)
        // Force reload to clean state and apply organization context globally
        window.location.href = '/'
    } catch (err) {
        console.error('Error confirming organization:', err)
        error.value = 'Erro ao selecionar organização'
    }
}

// Go back to login
const goBack = () => {
    router.push({ name: 'login' })
}

// Logout
const logout = () => {
    removeAuthCookies()
    removeStorageItems()
}

const formatDocument = (document: string): string => {
    if (!document) return ''

    // Remove all non-numeric characters
    const cleanDoc = document.replace(/\D/g, '')

    // Format based on length (CPF or CNPJ)
    if (cleanDoc.length === 11) {
        return cleanDoc.replace(/(\d{3})(\d{3})(\d{3})(\d{2})/, '$1.$2.$3-$4')
    } else if (cleanDoc.length === 14) {
        return cleanDoc.replace(/(\d{2})(\d{3})(\d{3})(\d{4})(\d{2})/, '$1.$2.$3/$4-$5')
    }

    return document
}

// Lifecycle
onMounted(() => {
    loadOrganizations()
})
</script>

<style lang="scss" scoped>
$primary-color:#c46c2c;

.text-primary{
    color:$primary-color;
}

.border-primary{
    border-color:$primary-color;
}

.hover\:border-primary:hover{
    border-color:$primary-color;
}

.hover\:text-primary:hover{
    color:$primary-color;
}

/* Custom animations */
@keyframes fadeIn {
    from {
        opacity: 0;
        transform: translateY(10px);
    }

    to {
        opacity: 1;
        transform: translateY(0);
    }
}

.space-y-4 > * {
    animation: fadeIn 0.6s ease-out;
    animation-fill-mode: both;
}

.space-y-4 > *:nth-child(1) {
    animation-delay: 0.1s;
}

.space-y-4 > *:nth-child(2) {
    animation-delay: 0.2s;
}

.space-y-4 > *:nth-child(3) {
    animation-delay: 0.3s;
}

.space-y-4 > *:nth-child(4) {
    animation-delay: 0.4s;
}

/* Smooth transitions for interactive elements */
button {
    transition: all 0.2s ease-in-out;
}

/* Loading animation for skeleton */
@keyframes pulse {
    0%,
    100% {
        opacity: 1;
    }

    50% {
        opacity: 0.5;
    }
}

.animate-pulse {
    animation: pulse 2s cubic-bezier(0.4, 0, 0.6, 1) infinite;
}
</style>
